import { Controller } from '@hotwired/stimulus';
import { refreshCsrfToken } from '../lib/fresh_csrf';
import { trackShown } from '../lib/funnel';

/**
 * Email-only unlock box on a marketing page (tool_email_unlocks/_box, ToolEmailUnlocksController).
 *
 * Sits on the page root so one controller serves every copy of the box (an A/B test can render
 * it more than once, hidden copies included) and the page's other "Get it" links.
 *
 * - warm: first pointer or focus on a box. Starts this visitor's session the way the tool signup
 *   dialog does (?pending_unlock=<slug>, so Continue with Google unlocks after the popup), then
 *   the page-wide token, so the Google popup can open without waiting. Also mounts Turnstile,
 *   which stays out of the cached page until someone actually reaches for the box.
 * - submit: posts the form with X-Inline-Unlock and swaps the box for the fragment that comes back
 *   (the sheet link, "check your inbox", or the box again with an error).
 * - jump: a "Get it" link elsewhere on the page scrolls to the visible box and focuses the email.
 */
export default class extends Controller {
  static values = { pendingUrl: String, returnTo: String };

  warm(event) {
    this.mountTurnstile(event.currentTarget);
    if (this.warming) return;

    this.warming = (async () => {
      if (this.pendingUrlValue) await refreshCsrfToken(this.pendingUrlValue, { returnTo: this.returnToValue });
      const pageUrl = document.querySelector('meta[name="csrf-fresh-url"]')?.content;
      if (pageUrl) await refreshCsrfToken(pageUrl);
    })();
  }

  async submit(event) {
    // edge_page_csrf holds the first submit until a token is in place, then submits again.
    if (event.defaultPrevented) return;
    event.preventDefault();

    const form = event.target;
    const box = form.closest('[data-inline-unlock-box]');
    if (!box || form.dataset.busy === 'true') return;

    form.dataset.busy = 'true';
    box.setAttribute('aria-busy', 'true');
    this.mountTurnstile(box);

    try {
      await this.warming;
      await this.turnstileReady(form);
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'X-Inline-Unlock': '1' },
      });
      if (!response.ok && response.status !== 422 && response.status !== 429) throw new Error(`HTTP ${response.status}`);

      const parent = box.parentElement;
      box.outerHTML = await response.text();
      // The fetched fragment fires no Turbo load event, so count the after-unlock offer here.
      parent?.querySelectorAll('[data-track-upsell]').forEach(trackShown);
    } catch {
      // Network or server error: the plain post still unlocks, on the tool page.
      delete form.dataset.busy;
      HTMLFormElement.prototype.submit.call(form);
    }
  }

  jump(event) {
    const box = [...this.element.querySelectorAll('[data-inline-unlock-box]')].find((el) => el.offsetParent !== null);
    if (!box) return;

    event.preventDefault();
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    box.querySelector('input[type="email"]')?.focus({ preventScroll: true });
  }

  mountTurnstile(box) {
    const template = box?.querySelector('template[data-inline-unlock-turnstile]');
    if (template) template.replaceWith(template.content.cloneNode(true));
  }

  // Interaction-only Turnstile fills its token a moment after it mounts; give it up to 5s.
  async turnstileReady(form) {
    if (!form.querySelector('.cf-turnstile')) return;

    for (let i = 0; i < 50; i += 1) {
      if (form.querySelector('input[name="cf-turnstile-response"]')?.value) return;
      await new Promise((resolve) => { setTimeout(resolve, 100); });
    }
  }
}
